import { useState } from "react"
import { coreRequest } from "services/requests"
import {
  DEVICE_TYPES,
  SCREEN_ID,
  SMASH_SESSION,
  WEB_APP_VERSION,
} from "common/constants"
import secureLocalStorage from "react-secure-storage"
import moment from "moment"
import Bowser from "bowser"
import { useGlobalAlert } from "contexts/GlobalAlertContext"
import { t } from "i18next"
import { isRunningFromIframe } from "common/functions"
import { jwtDecode } from "jwt-decode"
const FormData = require("form-data")

const prefix = "technicalSupport"
const sendRequest = coreRequest.sendRequest
const sendFormData = coreRequest.sendFormData

const useApi = () => {
  const [isFetching, setIsFetching] = useState(false)
  const { showAlert } = useGlobalAlert()

  const getDeviceSignature = () => {
    // this is from .net portal
    const runningFromIframe = isRunningFromIframe()
    if (runningFromIframe) {
      return localStorage.getItem("deviceSignature")
    } else {
      const smashSignature = secureLocalStorage.getItem("smashSignature")
      if (smashSignature !== null) {
        return smashSignature
      } else {
        const str = "0123456789ABCDEF"
        const uuid = []
        for (let i = 0; i < 8; i++) {
          uuid.push(str[Math.floor(Math.random() * str.length)])
        }
        uuid.push("-")
        for (let i = 0; i < 4; i++) {
          uuid.push(str[Math.floor(Math.random() * str.length)])
        }
        uuid.push("-")
        for (let i = 0; i < 4; i++) {
          uuid.push(str[Math.floor(Math.random() * str.length)])
        }
        uuid.push("-")
        for (let i = 0; i < 8; i++) {
          uuid.push(str[Math.floor(Math.random() * str.length)])
        }
        try {
          secureLocalStorage.setItem("smashSignature", uuid.join(""))
        } catch (error) {
          console.log("🚀 ~ getDeviceSignature ~ error:", error)
        }
        return uuid.join("")
      }
    }
  }

  const getScreenId = (screenName) => {
    const deviceTypeId = DEVICE_TYPES.WEB
    return SCREEN_ID[deviceTypeId][screenName]
  }

  const getSupportSections = async () => {
    setIsFetching(true)
    const deviceTypeId = DEVICE_TYPES.WEB
    const response = await sendRequest({
      endpoint: `technicalSupport/${deviceTypeId}`,
      method: "get",
    })
    if (!response.success) showAlert(response.message)
    setIsFetching(false)
    return response.data
  }

  const addTechnicalSupport = async ({
    supportTypeId,
    screen,
    relatedId = 0,
    subRelatedId = 0,
    screenShot,
    comment,
    imageFile,
    groupId,
    gradeId,
    audioFile,
    newGrade,
  }) => {
    setIsFetching(true)
    const screenId = getScreenId(screen)
    const { os } = Bowser.parse(window.navigator.userAgent)
    const deviceTypeId = DEVICE_TYPES.WEB
    // get os version
    const osVersion = `${os.name || ""} ${os.version || ""}`
    const appVersion = WEB_APP_VERSION
    const data = new FormData()
    data.append("deviceTypeId", deviceTypeId)
    data.append("supportTypeId", supportTypeId)
    data.append("screenId", screenId)
    data.append("relatedId", relatedId)
    data.append("subRelatedId", subRelatedId)
    data.append("osVersion", osVersion)
    data.append("appVersion", appVersion)
    if (groupId) {
      data.append("groupId", groupId)
    }
    if (gradeId) {
      data.append("gradeId", gradeId)
    }
    if (screenShot) {
      data.append("screenShot", screenShot)
    }
    if (newGrade) {
      data.append("newGrade", newGrade)
    }
    const response = await sendFormData({
      endpoint: `technicalSupport`,
      formData: data,
    })
    if (!response.success) {
      showAlert(response.message)
    } else {
      const reportId = response.data.reportId
      const result = await addReplyReport({
        reportId,
        comment,
        imageFile,
        audioFile,
      })
      setIsFetching(false)
      return result.success
    }
    return true
  }

  const addReplyReport = async ({
    reportId,
    comment,
    imageFile,
    audioFile,
  }) => {
    setIsFetching(true)
    const data = new FormData()
    if (imageFile) {
      data.append("imageFile", imageFile)
    }
    if (audioFile) {
      console.log(audioFile)
      data.append("audioFile", audioFile)
    }
    data.append("reportId", reportId)
    data.append("comment", comment)
    const response = await sendFormData({
      endpoint: `technicalSupport/reply`,
      formData: data,
    })
    if (!response.success) showAlert(response.message)
    setIsFetching(false)
    return response
  }

  const updateGrade = async ({
    grade,
    gradeId,
    comment,
    audioFile,
    viewTypeId,
  }) => {
    setIsFetching(true)
    const data = new FormData()
    data.append("grade", grade)
    data.append("gradeId", gradeId)
    data.append("comment", comment)
    if (audioFile) {
      data.append("audioFile", audioFile)
    }
    let parentToken
    try {
      const runningFromIframe = isRunningFromIframe()
      if (runningFromIframe) {
        parentToken = window?.parent?.getToken()
      } else {
        parentToken = secureLocalStorage.getItem("smashAuthToken")
      }
    } catch (error) {}
    const response = await sendFormData({
      endpoint: `grades/teacher/update`,
      formData: data,
      headers: {
        Authorization: parentToken,
      },
    })
    if (!response.success) showAlert(t(response.message))
    setIsFetching(false)
    return response.data
  }

  const newSession = async () => {
    // remove session log
    secureLocalStorage.removeItem(SMASH_SESSION)
    const deviceTypeId = DEVICE_TYPES.WEB
    const deviceSignature = getDeviceSignature()
    const { browser, os, platform } = Bowser.parse(window.navigator.userAgent)
    const deviceSO = `${os.name || ""} ${os.version || ""}`
    const deviceName = `${browser.name || ""} ${browser.version || ""} ${
      platform.type || ""
    }`
    const deviceDetails = JSON.stringify({
      react_version: process.env.REACT_APP_VERSION,
    })
    const runningFromIframe = isRunningFromIframe()
    // get version from web parent
    const appVersion =
      runningFromIframe &&
      typeof window?.parent?.getCurrentVersion === "function"
        ? window?.parent?.getCurrentVersion()
        : process.env.REACT_APP_VERSION

    const response = await sendRequest({
      endpoint: `sessionLog/createSession`,
      body: {
        deviceTypeId,
        deviceSignature,
        deviceName,
        deviceSO,
        deviceDetails,
        appVersion,
      },
      method: "post",
    })
    // console.log(SMASH_SESSION, "createSession RESPONE", response)
    if (!response.success) {
      console.log(response.message)
    } else {
      // get token from localStorage
      const authToken = secureLocalStorage.getItem("smashAuthToken")
      let userId
      if (authToken && authToken !== "") {
        try {
          const decodedPayload = jwtDecode(authToken)
          userId = decodedPayload?.id
        } catch (error) {
          console.error("Token inválido", error)
        }
      }
      secureLocalStorage.setItem(
        SMASH_SESSION,
        JSON.stringify({
          sessionTime: moment().valueOf(),
          activityLogId: response.data.activityLogId,
          date: response.data.date,
          userId,
        })
      )
    }
  }

  const createSession = async () => {
    setIsFetching(true)
    const session = secureLocalStorage.getItem(SMASH_SESSION)
    if (session !== null) {
      const sessionLog = JSON.parse(session)
      if (sessionLog) {
        try {
          const now = moment()
          const then = moment(sessionLog.sessionTime)
          const diff = now.diff(then, "minutes")
          if (diff > 60) {
            // console.log("session expired, create new session")
            await newSession()
          } else {
            // update session time
            sessionLog.sessionTime = moment().valueOf()
            secureLocalStorage.setItem(
              SMASH_SESSION,
              JSON.stringify(sessionLog)
            )
          }
        } catch (error) {
          await newSession()
        }
      }
    } else {
      // console.log(SMASH_SESSION, "SESSION DON'T EXISTS, CREATE NEW ONE")
      await newSession()
    }
    setIsFetching(false)
    return true
  }

  const addSessionLog = async ({
    view = "default",
    missionsActivityId = 0,
    exerciseId = 0,
  }) => {
    setIsFetching(true)
    let session = secureLocalStorage.getItem(SMASH_SESSION)
    if (
      !session ||
      session === null ||
      session === undefined ||
      session === ""
    ) {
      await newSession()
      session = secureLocalStorage.getItem(SMASH_SESSION)
    }
    // console.log("~ addSessionLog ~ session:", session)
    if (session !== null) {
      const viewId = view !== null ? getScreenId(view) : 0
      const sessionLog = JSON.parse(session)
      // console.log("~ addSessionLog ~ sessionLog:", sessionLog)
      const activityLogId = sessionLog.activityLogId
      let previousSessionLog = sessionLog.previousSessionLog
        ? sessionLog.previousSessionLog
        : null
      const response = await sendRequest({
        endpoint: `sessionLog/addSessionLog`,
        body: {
          viewId,
          activityLogId,
          previousSessionLog,
          missionsActivityId,
          exerciseId,
        },
        method: "post",
      })
      if (!response.success) {
        console.warn("error:" + response.message)
      } else {
        previousSessionLog = response.data
        sessionLog.previousSessionLog = previousSessionLog
        secureLocalStorage.setItem(SMASH_SESSION, JSON.stringify(sessionLog))
      }
      setIsFetching(false)
      return true
    }
    setIsFetching(false)
    return false
  }

  const getTechnicalReports = async () => {
    setIsFetching(true)
    const response = await sendRequest({
      endpoint: `${prefix}/reports`,
      method: "get",
    })
    if (!response.success) showAlert(response.message)
    setIsFetching(false)
    return response.data
  }

  const getTechnicalReportReplies = async ({ reportId }) => {
    setIsFetching(true)
    const response = await sendRequest({
      endpoint: `${prefix}/reports/${reportId}`,
      method: "get",
    })
    if (!response.success) showAlert(response.message)
    setIsFetching(false)
    return response.data
  }

  const openCloseTechnicalReport = async ({
    reportId,
    closeReport = false,
  }) => {
    setIsFetching(true)
    const response = await sendRequest({
      endpoint: `${prefix}/status`,
      body: {
        reportId,
        closeReport,
      },
      method: "post",
    })
    if (!response.success) showAlert(response.message)
    setIsFetching(false)
    return response.data
  }

  return {
    getSupportSections,
    addTechnicalSupport,
    addReplyReport,
    updateGrade,
    addSessionLog,
    createSession,
    getTechnicalReports,
    getTechnicalReportReplies,
    openCloseTechnicalReport,
    isFetching,
  }
}

export default useApi
